import { Button, Col, Drawer, message, Popover, Row, Select, Spin, Tabs, Image } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { handlers } from '../../../../helpers/helpers';
import { callPatient, getAsset } from '../../../../services/general';
import { getExpertExamRecord } from '../../../../services/exam';
import { getCookie } from '../../../../services/cookies';
import useSignatureData from '../../hooks/useSignatureData';
import useHandleSignature from '../../hooks/useHandleSignature';
import ExamSignatureDetail from './ExamSignatureDetail';
import SignatureInputs from './SignatureInputs';
import { useTranslation } from 'react-i18next';
import medicalConsultationCountrySettings from '../../../MedicalConsultationPage/countrySettings.json';
import countrySettings from '../../countrySettings.json';
import ExamDetailPage from '../../ExamDetailPage';
import moment from 'moment';
import { HasAccess } from '@permify/react-role';
import { useBoxContext } from '../../../../context/BoxContext';
import showOccupiedPatientModal from '../OccupiedPatientModal';
import showBlockedCallModal from '../BlockedCallModal';
import evaluationTypes from '../../../../utils/evaluationType.json';
import Notes from '../../../components/Notes';
import History from '../../../components/History';
import { EXAM_SPECIALTY_IDS } from '../../../MedicalConsultationPage/constants/examSpecialties';

const { Option } = Select;
const { TabPane } = Tabs;

const buildExpertExamHistoryParams = (admissionPatientExamId) => ({
  data: { type: 'expert_exam_actions', id: admissionPatientExamId },
  types: [
    'admission_patient_exams',
    'admission_expert_field_results',
    'consultation_diagnostics',
  ],
});

const buildExamResultsHistoryParams = (admissionPatientExamId) => ({
  data: { type: 'exam_results', id: admissionPatientExamId },
  types: [
    'admission_patient_exams',
    'admission_procedure_results',
    'admission_measurement_results',
    'admission_question_results',
    'exams_documents',
  ],
});

const computeAutoFillDefaults = (examData, formData, stateValue) => {
  if (!examData?.extra_data?.expertAutoFill?.cases || !stateValue || !formData?.expert_fields) {
    return {};
  }

  const cases = examData.extra_data.expertAutoFill.cases;
  const matchingCase = cases.find(caseItem => caseItem.stateValue === stateValue);
  if (!matchingCase) return {};

  const caseLabel = matchingCase.label;
  const defaults = {};

  formData.expert_fields.forEach(field => {
    if (field.extra_data?.autoValues?.[caseLabel]) {
      defaults[field.id] = field.extra_data.autoValues[caseLabel];
    }
  });

  return defaults;
};

const PatientHeaderGrid = ({ headerData, countryCode }) => {
  const [patientIdCard, setPatientIdCard] = useState(null);
  const [loadingIdCard, setLoadingIdCard] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchPatientIdCard = async () => {
      if (headerData?.id) {
        setLoadingIdCard(true);
        try {
          const response = await getAsset({ id: headerData.id, type: 'Patient', field: 'document' });
          setPatientIdCard(response?.document_url || null);
        } catch (error) {
          setPatientIdCard(null);
          console.error(error);
          message.error({
            content: 'Error al obtener la imagen del paciente',
            style: {
              marginTop: '40vh'
            }
          }); 
        } finally {
          setLoadingIdCard(false);
        }
      }
    };
    fetchPatientIdCard();
  }, [headerData?.id]);

  let idCardTemplate;
  if (loadingIdCard) {
    idCardTemplate = (
      <Row justify="center">
        <Spin className="cmt-spin custom-spinner" />
      </Row>
    );
  } else if (patientIdCard && !patientIdCard.includes('missing.png')) {
    idCardTemplate = (
      <div className="exam-detail-drawer__patient-avatar">
        <Image
          src={patientIdCard}
          alt={`Documento de identidad del ${t('medicalConsultations.header.patientTitle').toLocaleLowerCase()}`}
        />
      </div>
    );
  } else {
    idCardTemplate = (
      <div className="exam-detail-drawer__patient-avatar">
        <Image
          src={'data:image/png;base64,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'}
          alt="Documento de identidad del paciente"
        />
      </div>
    );
  }



  const headerFields = {
    id_card: {
      isTemplate: true,
      content: (
        <div className="id-card-wrapper" style={{ display: 'flex', justifyContent: 'center' }}>
          {idCardTemplate}
        </div>
      )
    },
    full_name: {
      label: 'Nombres',
      value: headerData?.full_name
    },
    company: {
      label: 'Empresa',
      value: headerData?.company || headerData?.business_name
    },
    profession: {
      label: 'Puesto',
      value: headerData?.profession || headerData?.position
    },
    reservation: {
      label: 'N° de Cita',
      value: headerData?.reservation_number
    },
    age: {
      label: 'Edad',
      value: headerData?.age
    },
    birthdate: {
      label: 'Fecha de nacimiento',
      value: headerData?.birthdate ? moment(headerData.birthdate).format('DD/MM/YYYY') : null
    },
    document: {
      label: handlers.documentType(headerData?.document_type?.toLowerCase()),
      value: headerData?.document_number
    },
    outsourcing: {
      label: 'Subcontrata',
      value: headerData?.outsourcing_company || headerData?.outsourcing || headerData?.outsourcing_name
    },
    third_company: {
      label: 'Tercera empresa',
      value: headerData?.third_company || headerData?.third_company_name
    },
    project: {
      label: 'Proyecto',
      value: headerData?.project || headerData?.project_name
    },
    area: {
      label: 'Área',
      value: headerData?.area || headerData?.area_name
    },
    external_id: {
      label: 'ID Externo',
      value: headerData?.external_id
    },
    evaluation_type: {
      label: 'Tipo de evaluación',
      value: (headerData?.evaluation_type || headerData?.evaluation_type === 0) ? (evaluationTypes[countryCode]?.find(type => type.value.toString() === headerData?.evaluation_type.toString())?.text || headerData?.evaluation_type) : null
    },
    educational_lvl: {
      label: 'Grado de instrucción',
      value: headerData?.educational_lvl
    }
  };

  const headerLayouts = {
    DEFAULT: {
      top: [
        { lg: 3, fields: ['id_card'] },
        { lg: 9, fields: ['full_name'] },
        { lg: 6, fields: ['document'] },
        { lg: 6, fields: ['age'] }
      ],
      bottom: [
        { lg: 12, fields: ['company'] },
        { lg: 6, fields: ['profession'] },
        { lg: 6, fields: ['reservation'] }
      ]
    },
    PE: {
      top: [
        { lg: 3, fields: ['id_card'] },
        { lg: 8, fields: ['full_name', 'birthdate'] },
        { lg: 6, fields: ['document', 'age'] },
        { lg: 7, fields: ['educational_lvl'], alignTop: true }
      ],
      bottom: [
        { lg: 10, fields: ['company', 'outsourcing', 'third_company'] },
        { lg: 7, fields: ['project', 'area', 'profession'] },
        { lg: 7, fields: ['reservation', 'evaluation_type', 'external_id'] }
      ]
    }
  };

  const layout = countrySettings.customHeaderLayout[countryCode] ? headerLayouts[countryCode] : headerLayouts.DEFAULT;

  const renderField = (fieldKey, lg, index) => {
    const field = headerFields[fieldKey];
    if (!field) return null;
    if (field.isTemplate) {
      return <div key={index}>{field.content}</div>;
    }
    return (
      <div key={index} className="exam-detail-drawer__header-field">
        <span className="exam-detail-drawer__header-label">{field.label}:</span>
        <strong className="exam-detail-drawer__header-value">
          {field.value || '-'}
        </strong>
      </div>
    );
  };

  return (
    <div className="exam-detail-drawer__custom-header pt-20 pb-20 px-20">
      <Row wrap={true} gutter={[20, 0]} align="middle">
        {layout.top.map((column, colIndex) => (
          <Col lg={column.lg} xs={24} key={`top-${colIndex}`} className={column.alignTop ? 'exam-detail-drawer__col-align-top' : ''}>
            {column.fields.map((fieldKey, fieldIndex) => renderField(fieldKey, column.lg, fieldIndex))}
          </Col>
        ))}
      </Row>
      <div className="header-divider mt-10 mb-10" />
      <Row wrap={true} gutter={[20, 0]}>
        {layout.bottom.map((column, colIndex) => (
          <Col lg={column.lg} xs={24} key={`bottom-${colIndex}`}>
            {column.fields.map((fieldKey, fieldIndex) => renderField(fieldKey, column.lg, fieldIndex))}
          </Col>
        ))}
      </Row>
    </div>
  );
};

const ExamDetailDrawerContent = forwardRef(({
  record,
  onExamSaved,
  onStatusLoad,
  onLoadingChange,
  onGlobalSubmit,
  isAnyLoading,
  isAnySigned,
}, ref) => {
  const { t } = useTranslation();
  const { hasBoxes } = useBoxContext();
  const countryCode = getCookie('country_code');

  const admissionPatientId = record.admission_patient_id;
  const admissionPatientExamId = record.admission_patient_exam;

  const {
    loading: dataLoading,
    headerData,
    examData,
    extraExams,
  } = useSignatureData(
    admissionPatientId,
    admissionPatientExamId,
    t,
  );

  const {
    loading: signatureLoading,
    formData,
    updateExamSignature,
    saveExpertFields,
  } = useHandleSignature(admissionPatientExamId);

  const signatureInputsRef = useRef(null);

  useImperativeHandle(ref, () => ({
    validate: async ({ sign }) => {
      if (sign && stateField && !selectedState) {
        throw new Error('Debe seleccionar un estado antes de firmar');
      }
      if (signatureInputsRef.current) {
        return await signatureInputsRef.current.validateAndGetPayload({ sign });
      }
      throw new Error('La información de esta pestaña aún está cargando. Por favor, espere un momento.');
    },
    save: async (payload, { sign }) => {
      await handleSaveAllExams();
      await saveExpertFields(payload, { sign, stateField, selectedState });
    }
  }));

  const [documentUrl, setDocumentUrl] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [selectedState, setSelectedState] = useState(undefined);
  const [loadingCallPatient, setLoadingCallPatient] = useState(false);

  const handleCallPatient = async (forceOccupiedCall = false, forceRecentCall = false) => {
    const box = getCookie('box_id');
    if (!box || typeof box !== 'string' || box.trim() === '') {
      message.warning(t('examPage.examTabs.callPatientBoxWarning'));
      return;
    }

    setLoadingCallPatient(true);

    try {
      await callPatient({ admissionPatientId: Number(admissionPatientId), box, forceOccupiedCall, forceRecentCall });
      message.success(t('examPage.examTabs.callPatientSuccess'));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error?.message || error?.toString?.() || 'Error';

      if (errorMessage?.includes('PATIENT_OCCUPIED_IN_EXAM') && !forceOccupiedCall) {
        setLoadingCallPatient(false);
        showOccupiedPatientModal(errorMessage, () => handleCallPatient(true, false), t);
        return;
      }

      if (errorMessage?.includes('PATIENT_RECENTLY_CALLED') && !forceRecentCall) {
        setLoadingCallPatient(false);
        showBlockedCallModal(errorMessage, () => handleCallPatient(forceOccupiedCall, true), t);
        return;
      }

      message.error(errorMessage || t('examPage.examTabs.callPatientError'));
      console.error('Error handleCallPatient:', error);
    } finally {
      setLoadingCallPatient(false);
    }
  };
  const examSaveHandlersRef = useRef({});
  const [saveAndSignLoading, setSaveAndSignLoading] = useState(false);
  const [expertExamRecord, setExpertExamRecord] = useState(record);

  const [isExamPageLoading, setIsExamPageLoading] = useState(true);

  const isContentLoading = dataLoading.header || dataLoading.exam || signatureLoading.form || signatureLoading.saved || saveAndSignLoading || documentLoading || isExamPageLoading;

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(record.admission_patient_exam, isContentLoading);
    }
  }, [isContentLoading, record.admission_patient_exam, onLoadingChange]);

  useEffect(() => {
    if (onStatusLoad && formData) {
      onStatusLoad(record.admission_patient_exam, formData.status);
    }
  }, [formData?.status, record.admission_patient_exam, onStatusLoad]);


  useEffect(() => {
    const fetchExpertExamRecord = async () => {
      try {
        const fullRecord = await getExpertExamRecord(admissionPatientExamId);
        if (fullRecord) setExpertExamRecord(fullRecord);
      } catch (error) {
        console.error('Error fetching expert exam record:', error);
        message.error({
          content: 'Error al cargar datos del examen',
          style: {
            marginTop: '45vh',
          },
        });
      }
    };
    fetchExpertExamRecord();
  }, [admissionPatientExamId]);

  const formattedDocument = headerData?.document_type === 'rut'
    ? handlers.formatDocument(headerData?.document_number)
    : headerData?.document_number;


  const stateField = useMemo(() => {
    if (!formData?.expert_fields) return null;
    return formData.expert_fields.find(expertField => expertField.code?.startsWith('ESTADO_')) || null;
  }, [formData?.expert_fields]);


  useEffect(() => {
    if (stateField) {
      setSelectedState(stateField.result || stateField.default_value || undefined);
    }
  }, [stateField]);

  const autoFillDefaults = useMemo(() => {
    return computeAutoFillDefaults(examData, formData, selectedState);
  }, [examData, formData, selectedState]);
  const handleStateChange = (value) => {
    setSelectedState(value);
  };

  const handleSaveAllExams = useCallback(async () => {
    const promises = Object.values(examSaveHandlersRef.current).map(saveExamTabHandler => saveExamTabHandler?.());
    await Promise.all(promises);
  }, []);


  const handleSubmit = useCallback(async (finalExamData, { sign = false } = {}) => {
    if (sign && stateField && !selectedState) {
      message.warning({
        content: 'Debe seleccionar un estado antes de firmar',
        className: 'signature-message',
      });
      return;
    }
    setSaveAndSignLoading(true);
    try {
      await handleSaveAllExams();
      await saveExpertFields(finalExamData, { sign, stateField, selectedState });
      if (onExamSaved) onExamSaved();
    } catch (error) {
      console.error(`Error  ${sign ? 'saving and signing' : 'saving'}:`, error);
      message.error({
        content: `Error al ${sign ? 'guardar y firmar' : 'guardar'}`,
        style: {
          marginTop: '40vh'
        }
      }); 
    } finally {
      setSaveAndSignLoading(false);
    }
  }, [handleSaveAllExams, stateField, selectedState, saveExpertFields, onExamSaved]);

  const viewableDocumentId = expertExamRecord?.viewableFiles?.[0]?.documentResultId 
    || expertExamRecord?.documentResultId 
    || null;

  const hasViewableDocuments = expertExamRecord?.hasViewableDocuments ?? false;

  useEffect(() => {
    const fetchDocumentAsset = async () => {
      if (!viewableDocumentId) {
        setDocumentUrl(null);
        return;
      }
      try {
        setDocumentLoading(true);
        const response = await getAsset({
          field: 'document',
          id: viewableDocumentId,
          type: 'AdmissionProcedureResult',
        });
        const resolvedUrl = response?.document_url || null;
        setDocumentUrl(resolvedUrl);
      } catch (error) {
        console.error('Error loading document on detail drawer:', error);
        message.error({
          content: `Error al cargar documento en detalle.`,
          style: {
            marginTop: '40vh'
          }
        }); 
        setDocumentUrl(null);
      } finally {
        setDocumentLoading(false);
      }
    };

    fetchDocumentAsset();
  }, [viewableDocumentId]);

  const isLoading = dataLoading.header || dataLoading.exam;


  const handleExamSaved = () => {
    if (onExamSaved) onExamSaved();
  };

  return (
    <Spin className="cmt-spin" spinning={isLoading}>

      { ([examData?.code, record?.exam_code].includes('CONSULTA_PSICO')) ? (
        <>
          <Row gutter={[20, 16]}>
            <Col span={24}>
              <PatientHeaderGrid headerData={headerData} countryCode={countryCode} />
            </Col>
          </Row>
          <Row gutter={[20, 16]} className="mb-20 mt-10" align="middle" justify="space-between">
            <Col>
              <div className="exam-detail-drawer__status-section">
                <div className="exam-detail-drawer__status-field">
                  {stateField && <p className="exam-detail-drawer__status-label mb-5">Estado</p>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {stateField ? (
                      <Select
                        className="cmt-select exam-detail-drawer__state-select"
                        value={selectedState}
                        placeholder="Sin estado"
                        onChange={handleStateChange}
                        style={{ width: '200px' }}
                      >
                        {stateField.options?.map((option, index) => (
                          <Option key={index} value={option}>
                            {option}
                          </Option>
                        ))}
                      </Select>
                    ) : null}
                    {countryCode === 'PE' && hasBoxes && (() => {
                      const admissionDate = headerData?.admission_date || record?.admission_date;
                      const isToday = admissionDate ? moment().isSame(admissionDate, 'day') : true;
                      const isDisabled = !isToday || loadingCallPatient;
                      return (
                        <HasAccess
                          roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager']}
                          permission="call-patient"
                        >
                          <Popover
                            content={!isToday ? t('examPage.examTabs.callPatientDisabledTooltip') : ''}
                            placement="top"
                          >
                            <span>
                              <Button
                                loading={loadingCallPatient}
                                disabled={isDisabled}
                                onClick={() => handleCallPatient()}
                                className="cmt-button secondary"
                                type="primary"
                                size="middle"
                                style={isDisabled ? { pointerEvents: 'none' } : {}}
                              >
                                {t('examPage.examTabs.callPatient')}
                              </Button>
                            </span>
                          </Popover>
                        </HasAccess>
                      );
                    })()}
                    <History params={buildExpertExamHistoryParams(admissionPatientExamId)} />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </>
      ) : (
        <Row gutter={[20, 16]} className="mb-20">
          <Col span={13}>
            <div className="exam-detail-drawer__patient-card">
              <p>
                Trabajador: <strong>{headerData?.full_name || ''}</strong>
              </p>
              <p>
                DNI: <strong>{formattedDocument}</strong>
              </p>
              {headerData?.age && (
                <p>
                  Edad: <strong>{headerData.age}</strong>
                </p>
              )}
            </div>
          </Col>

          <Col span={11}>
            <div className="exam-detail-drawer__status-section">
              <div className="exam-detail-drawer__status-field">
                {stateField && <p className="exam-detail-drawer__status-label mb-5">Estado</p>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {stateField ? (
                    <Select
                      className="cmt-select exam-detail-drawer__state-select"
                      value={selectedState}
                      placeholder="Sin estado"
                      onChange={handleStateChange}
                      style={{ flex: 1 }}
                    >
                      {stateField.options?.map((option, index) => (
                        <Option key={index} value={option}>
                          {option}
                        </Option>
                      ))}
                    </Select>
                  ) : null}
                  {countrySettings.callPatient[countryCode] && hasBoxes && (() => {
                    const admissionDate = headerData?.admission_date || record?.admission_date;
                    const isToday = admissionDate ? moment().isSame(admissionDate, 'day') : true;
                    const isDisabled = !isToday || loadingCallPatient;
                    return (
                      <HasAccess
                        roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager']}
                        permission="call-patient"
                      >
                        <Popover
                          content={!isToday ? t('examPage.examTabs.callPatientDisabledTooltip') : ''}
                          placement="top"
                        >
                          <span>
                            <Button
                              loading={loadingCallPatient}
                              disabled={isDisabled}
                              onClick={() => handleCallPatient()}
                              className="cmt-button secondary"
                              type="primary"
                              size="middle"
                              style={isDisabled ? { pointerEvents: 'none' } : {}}
                            >
                              {t('examPage.examTabs.callPatient')}
                            </Button>
                          </span>
                        </Popover>
                      </HasAccess>
                    );
                  })()}
                  <History params={buildExpertExamHistoryParams(admissionPatientExamId)} />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {examData?.name && (
        <Tabs className="cmt-tabs" defaultActiveKey="examDetail_0">
          {
            [examData, ...(extraExams || [])]?.map((exam, index) =>
              <TabPane key={`examDetail_${index}`} tab={exam.name} forceRender>
                <Row justify="space-between" align="middle">
                  <Col>
                    <div className="exam-detail-drawer__section-title">
                      {`Detalle de ${exam.name || 'examen'}`}
                    </div>
                  </Col>
                  <Col>
                    <History params={buildExamResultsHistoryParams(exam.admissionPatientExamId)} />
                  </Col>
                </Row>

                {
                  (exam?.procedures?.some((procedure) => procedure.input_type === 'file')) &&
                  hasViewableDocuments && examData?.exam_specialty_id !== EXAM_SPECIALTY_IDS[countryCode]?.ULTRASOUND &&
                  <>
                    <p className="exam-detail-drawer__section-subtitle">
                      Previsualizador de examen
                    </p>
                    <div className="exam-detail-drawer__image-preview">
                      {documentLoading ? (
                        <Spin size="large" />
                      ) : documentUrl ? (
                        <iframe
                          src={`${documentUrl}#toolbar=1`}
                          title={`Previsualizador de ${exam?.name || 'examen'}`}
                          loading="lazy"
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                          allow="fullscreen"
                        />
                      ) : (
                        <p style={{ color: '#bebebe', fontSize: '16px' }}>
                          No hay documento disponible
                        </p>
                      )}
                    </div>
                  </>
                }
                <ExamDetailPage
                  from={'expert'}
                  fromExternal={true}
                  countryCode={countryCode}
                  readOnly={exam.editable === false}
                  externalExam={{
                    admission_patient_id: exam.admission_patient_id,
                  }}
                  externalAdmissionPatientExamId={exam.admissionPatientExamId}
                  onExamSaveReady={(saveExamsFn) => {
                    if (exam.editable === false) {
                      delete examSaveHandlersRef.current[index];
                      return;
                    }
                    examSaveHandlersRef.current[index] = saveExamsFn;
                  }}
                  onLoadingChange={setIsExamPageLoading}
                  saveAndSignLoading={saveAndSignLoading}
                />
              </TabPane>
            )
          }
        </Tabs>
      )}

      {examData?.id && (
        <SignatureInputs
          ref={signatureInputsRef}
          id={admissionPatientExamId}
          loading={{ ...signatureLoading, saved: signatureLoading.saved || saveAndSignLoading }}
          formData={formData}
          examData={examData}
          updateExamSignature={async (finalExamData, type) => {
            if (stateField && selectedState) {
              if (!finalExamData.body.expertFields) {
                finalExamData.body.expertFields = [];
              }
              const existingIndex = finalExamData.body.expertFields.findIndex(expertField => expertField.id === stateField.id);
              if (existingIndex >= 0) {
                finalExamData.body.expertFields[existingIndex].result = selectedState;
              } else {
                finalExamData.body.expertFields.push({
                  ...stateField,
                  result: selectedState,
                });
              }
            }
            await updateExamSignature(finalExamData, type);
            handleExamSaved();
          }}
          countryCode={countryCode}
          patientFullName={record.full_name}
          countrySettings={medicalConsultationCountrySettings}
          t={t}
          isDrawer
          autoFillDefaults={autoFillDefaults}
          onSubmit={handleSubmit}
          onGlobalSubmit={onGlobalSubmit}
          isAnyLoading={isAnyLoading}
          isAnySigned={isAnySigned}
        />
      )}
    </Spin>
  );
});

const ExamDetailDrawerSpecialtyTabs = ({ record, onExamSaved }) => {
  const [activeTabKey, setActiveTabKey] = useState('0');
  const siblingExams = record?.siblingExams || [];

  const allExams = [
    { admissionPatientExamId: record.admission_patient_exam, examName: record.exam_name },
    ...siblingExams,
  ];

  const contentRefs = useRef({});
  const [loadingStates, setLoadingStates] = useState({});
  const [statusStates, setStatusStates] = useState({});
  const [globalSaving, setGlobalSaving] = useState(false);

  const handleStatusLoad = useCallback((examId, status) => {
    setStatusStates((prev) => ({ ...prev, [examId]: status }));
  }, []);

  const handleLoadingChange = useCallback((examId, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [examId]: isLoading }));
  }, []);

  const isAnyLoading = Object.values(loadingStates).some((loading) => loading) || globalSaving;
  const isAnySigned = Object.values(statusStates).some((status) => status === 'signed');

  const handleGlobalSubmit = async ({ sign }) => {
    if (isAnyLoading) return;

    const payloads = [];
    for (let i = 0; i < allExams.length; i++) {
      const examRef = contentRefs.current[i];
      if (examRef) {
        try {
          const payload = await examRef.validate({ sign });
          payloads.push({ ref: examRef, payload });
        } catch (error) {
          setActiveTabKey(String(i));
          message.warning({
            content: error?.message || 'Falta información en esta pestaña.',
            className: 'signature-message',
          });
          return;
        }
      }
    }

    setGlobalSaving(true);
    try {
      await Promise.all(payloads.map((p) => p.ref.save(p.payload, { sign })));
      if (onExamSaved) onExamSaved();
    } catch (error) {
      console.error(`Error ${sign ? 'saving and signing' : 'saving'}:`, error);
      message.error({
        content: `Error al ${sign ? 'guardar y firmar' : 'guardar'} globalmente`,
        style: {
          marginTop: '40vh'
        }
      });
    } finally {
      setGlobalSaving(false);
    }
  };

  return (
    <>
      <h2 className="exam-detail-drawer__title" style={{ marginBottom: 8 }}>
        {record.specialtyName || 'Otros'}
      </h2>
      <Tabs
        className="cmt-tabs exam-detail-drawer__specialty-tabs"
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
      >
        {allExams.map((exam, index) => (
          <TabPane key={String(index)} tab={exam.examName} forceRender={true}>
            <ExamDetailDrawerContent
              ref={(element) => (contentRefs.current[index] = element)}
              record={{
                ...record,
                admission_patient_exam: exam.admissionPatientExamId,
                exam_name: exam.examName,
              }}
              onExamSaved={onExamSaved}
              onStatusLoad={handleStatusLoad}
              onLoadingChange={handleLoadingChange}
              onGlobalSubmit={handleGlobalSubmit}
              isAnyLoading={isAnyLoading}
              isAnySigned={isAnySigned}
            />
          </TabPane>
        ))}
      </Tabs>
    </>
  );
};

const ExamDetailDrawer = ({
  open,
  record,
  onClose,
  onExamSaved,
}) => {
  return (
    <Drawer
      className="exam-detail-drawer"
      title={null}
      placement="right"
      width="74%"
      open={open}
      onClose={onClose}
      closable={true}
      destroyOnClose
    >
      {record && (
        <ExamDetailDrawerSpecialtyTabs
          record={record}
          onExamSaved={onExamSaved}
        />
      )}
    </Drawer>
  );
};

export default ExamDetailDrawer;
